import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/lib/auth";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Register() {
  const { t } = useTranslation();
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const planParam = sp.get("plan");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(email, password, name);
      toast.success(t("auth.accountCreated"));
      nav(planParam ? `/app/abonament?plan=${planParam}` : "/app/oferte");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("auth.registerError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#0B111D]">
      <div className="flex items-center justify-center p-8 lg:p-16 order-2 lg:order-1 relative">
        <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
        <form onSubmit={submit} className="w-full max-w-md fade-up" data-testid="register-form">
          <div className="overline mb-3">{t("auth.registerOverline")}</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("auth.registerTitle")}</h1>
          <p className="mt-2 text-sm text-[#94A3B8]">{t("auth.haveAccount")} <Link to={planParam ? `/login?plan=${planParam}` : "/login"} className="text-[#00BFFF] font-semibold hover:underline">{t("auth.signIn")}</Link></p>
          {planParam && (
            <div className="mt-4 p-3 bg-[#FBBF24]/10 border-l-2 border-[#FBBF24] text-sm" data-testid="plan-hint">
              ⚡ {t("auth.planHintRegister", { plan: planParam })}
            </div>
          )}
          <div className="mt-8 space-y-4">
            <div>
              <label className="overline block mb-2">{t("auth.nameLabel")}</label>
              <input data-testid="register-name" required value={name} onChange={(e)=>setName(e.target.value)} className="input-flat" placeholder={t("auth.namePlaceholder")}/>
            </div>
            <div>
              <label className="overline block mb-2">{t("auth.emailLabel")}</label>
              <input data-testid="register-email" type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} className="input-flat" placeholder={t("common.emailPlaceholder")}/>
            </div>
            <div>
              <label className="overline block mb-2">{t("auth.passwordLabel")}</label>
              <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e)=>setPassword(e.target.value)} className="input-flat" placeholder={t("auth.passwordMin")}/>
            </div>
          </div>
          <button type="submit" disabled={busy} className="btn-primary w-full mt-8 disabled:opacity-50" data-testid="register-submit">
            {busy ? t("auth.creatingAccount") : t("auth.registerBtn")}
          </button>
          <p className="mt-4 text-xs text-[#94A3B8]">{t("auth.freeNote")}</p>
        </form>
      </div>
      <div className="hidden lg:block relative bg-[#111C30] text-white p-12 order-1 lg:order-2">
        <Link to="/" className="flex items-center gap-3 font-display text-2xl font-extrabold tracking-tight">
          <img src="/brand/logo-icon.png" alt="IronOfferAI" className="w-11 h-11 rounded-lg"/>
          IronOfferAI
        </Link>
        <div className="absolute bottom-12 left-12 right-12">
          <div className="overline" style={{color: "#FBBF24"}}>{t("auth.sideOverlineRegister")}</div>
          <h2 className="font-display text-4xl font-extrabold mt-4">{t("auth.sideTitleRegister")}</h2>
          <p className="mt-4 text-white/60">{t("auth.sideDescRegister")}</p>
        </div>
        <div className="absolute top-12 right-12 w-24 h-24 bg-[#00BFFF]"/>
      </div>
    </div>
  );
}
